import {SerializedError} from '@reduxjs/toolkit'

const MAX_DETAIL_LENGTH = 200

/**
 * JSON-encode a value for a log line, degrading gracefully on cyclic or oversized input.
 * @param value - The value to encode
 * @returns A short, printable representation of the value
 */
const stringifyDetail = (value: unknown): string => {
  if (typeof value === 'string') return value

  let encoded: string | undefined
  try {
    encoded = JSON.stringify(value)
  } catch {
    return '[unserializable]'
  }

  if (encoded === undefined) return String(value)

  return encoded.length > MAX_DETAIL_LENGTH
    ? `${encoded.slice(0, MAX_DETAIL_LENGTH)}…`
    : encoded
}

/**
 * Describe whatever the SAPI client rejected with in one readable line.
 *
 * Rejections are not always Errors: RTK and fetch surface plain objects carrying `status`
 * and `data`. Interpolating those directly produced "[object Object]", which left the log
 * with nothing to triage on.
 * @param originalError - The rejection to describe
 * @returns A readable description of the rejection
 */
const describeOriginalError = (originalError: unknown): string => {
  if (originalError === null || originalError === undefined) {
    return 'Unknown error'
  }

  if (typeof originalError === 'string') return originalError

  if (originalError instanceof Error) return originalError.message

  if (typeof originalError !== 'object') return String(originalError)

  const {message, error, status, data} = originalError as Record<
    string,
    unknown
  >

  if (typeof message === 'string' && message) return message
  if (typeof error === 'string' && error) return error

  const parts: string[] = []
  if (status !== undefined) parts.push(`status ${stringifyDetail(status)}`)
  if (data !== undefined) parts.push(stringifyDetail(data))

  return parts.length > 0 ? parts.join(' ') : stringifyDetail(originalError)
}

/**
 * Custom error that we can narrow to in middleware
 * Prefixes message to the originalError message & appends all properties of the original error to "originalError"
 */
export class SearchApiError extends Error {
  originalError: Record<string, unknown>
  originalMessage: string

  constructor(message: string, originalError: unknown) {
    const originalMessage = describeOriginalError(originalError)

    super(`${message} - ${originalMessage}`)

    const {stack, ...originalErrorProps} =
      typeof originalError === 'object' && originalError !== null
        ? (originalError as Record<string, unknown>)
        : {message: originalError, stack: undefined}

    this.name = 'SearchApiError'
    this.originalMessage = originalMessage
    // Plain-object rejections carry no stack, so keep the one super() captured.
    if (typeof stack === 'string') this.stack = stack
    this.originalError = originalErrorProps
  }
}

const isSearchApiError = (error: unknown): error is SearchApiError =>
  error instanceof SearchApiError

/**
 * Type safe helper to get the message from a query hook `error` response
 * @param error - The error from a query hook response
 * @returns The message of the underlying error
 */
export const getSearchApiErrorMessage = (
  error: SearchApiError | SerializedError
) => (isSearchApiError(error) ? error.originalMessage : error.message)
